import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Upload, X, Loader2, FileText, Plus, Download } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = {
    convocazioni: "Convocazioni",
    bilanci_esercizio: "Bilanci di esercizio",
    statuto_sociale: "Statuto sociale",
    certificati: "Certificati",
    verbali_assemblea: "Verbali Assemblea",
    verbali_cda: "Verbali CDA",
    comunicazioni: "Comunicazioni",
    altro: "Altro",
};

export default function AdminDocumenti() {
    const [docs, setDocs] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [visibilityFilter, setVisibilityFilter] = useState("all"); // 'all' | 'individual' | 'group'

    const load = useCallback(() => {
        const params = visibilityFilter !== "all" ? `?visibility=${visibilityFilter}` : "";
        return apiPrivate.get(`/admin/documents${params}`).then(r => setDocs(r.data)).catch(() => {});
    }, [visibilityFilter]);
    useEffect(() => { load(); }, [load]);

    const revoke = async (id) => {
        if (!window.confirm("Disattivare questo documento?")) return;
        await apiPrivate.delete(`/admin/documents/${id}`);
        load();
    };

    const downloadDoc = async (doc) => {
        try {
            const res = await apiPrivate.get(`/documents/${doc.id}/download-url`);
            const { url, mode, filename } = res.data;
            if (mode === "presigned") {
                // Presigned S3: open direct, no auth header interference
                const a = document.createElement("a");
                a.href = url;
                a.download = filename || "documento";
                a.rel = "noopener noreferrer";
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                // Local stream fallback: fetch as blob (auth via apiPrivate)
                const blobRes = await apiPrivate.get(`/documents/${doc.id}/download`, { responseType: "blob" });
                const blobUrl = window.URL.createObjectURL(blobRes.data);
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = filename || doc.filename || "documento";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore durante il download");
        }
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-documenti">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Documenti</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">Archivio <span className="font-serif-italic text-acid">documenti</span>.</h1>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setShowImport(true)} className="btn-ghost" data-testid="btn-import-docs">
                            <Upload className="h-4 w-4" /> Import bulk
                        </button>
                        <button onClick={() => setShowUpload(true)} className="btn-rebel" data-testid="btn-upload-doc">
                            <Plus className="h-4 w-4" /> Pubblica documento
                        </button>
                    </div>
                </div>

                {/* Filtri visibilità */}
                <div className="flex gap-2 mt-6" data-testid="visibility-filter">
                    {[
                        { key: "all", label: "Tutti", count: null },
                        { key: "individual", label: "Individuali", count: null },
                        { key: "group", label: "Di gruppo", count: null },
                    ].map(f => (
                        <button
                            key={f.key}
                            onClick={() => setVisibilityFilter(f.key)}
                            className={`px-4 py-2 rounded-xl border-2 text-xs uppercase tracking-widest font-grotesk transition ${
                                visibilityFilter === f.key
                                    ? "border-magenta bg-magenta/10 text-white"
                                    : "border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-white"
                            }`}
                            data-testid={`filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <span className="ml-auto self-center text-xs text-zinc-500 font-grotesk">{docs.length} documenti</span>
                </div>

                <div className="bento mt-4 p-0 overflow-x-auto" data-testid="admin-docs-table">
                    <table className="w-full text-sm">
                        <thead className="border-b border-zinc-900 text-left">
                            <tr className="font-grotesk uppercase text-xs tracking-widest text-zinc-500">
                                <th className="p-4">Titolo</th>
                                <th className="p-4">Categoria</th>
                                <th className="p-4">Visibilità</th>
                                <th className="p-4">Destinatario</th>
                                <th className="p-4 text-right">Nº</th>
                                <th className="p-4">Data</th>
                                <th className="p-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.length === 0 && (
                                <tr><td colSpan={7} className="p-10 text-center text-zinc-500" data-testid="docs-empty-admin">Nessun documento pubblicato. Inizia ora.</td></tr>
                            )}
                            {docs.map(d => (
                                <tr key={d.id} className="border-b border-zinc-900" data-testid={`admin-doc-${d.id}`}>
                                    <td className="p-4 font-grotesk">
                                        <div className="flex items-center gap-3">
                                            <FileText className="h-4 w-4 text-zinc-500 shrink-0" />
                                            <span>{d.title}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-zinc-400">{CATEGORY_LABEL[d.category] || d.category}</td>
                                    <td className="p-4">
                                        {d.visibility_type === "individual" ? (
                                            <span className="text-magenta uppercase text-xs tracking-widest font-grotesk">Individuale</span>
                                        ) : (
                                            <div className="flex gap-1">{(d.quota_groups || []).map(q => <span key={q} className="bg-acid text-black text-[10px] font-bold px-2 py-0.5 font-grotesk">{q}</span>)}</div>
                                        )}
                                    </td>
                                    <td className="p-4 text-zinc-400 text-xs font-grotesk">
                                        {d.visibility_type === "individual"
                                            ? (d.owner_display || <span className="text-zinc-600 italic">utente rimosso</span>)
                                            : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="p-4 text-right font-mono text-acid">{d.recipients_count}</td>
                                    <td className="p-4 text-zinc-500 text-xs whitespace-nowrap">{new Date(d.published_at).toLocaleDateString("it-IT")}</td>
                                    <td className="p-4 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-3">
                                            <button
                                                onClick={() => downloadDoc(d)}
                                                className="text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-bayside inline-flex items-center gap-1"
                                                data-testid={`download-${d.id}`}
                                                title="Scarica documento"
                                            >
                                                <Download className="h-3.5 w-3.5" /> Scarica
                                            </button>
                                            {d.is_active && (
                                                <button
                                                    onClick={() => revoke(d.id)}
                                                    className="text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-magenta"
                                                    data-testid={`revoke-${d.id}`}
                                                >
                                                    Revoca
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
                {showImport && <ImportBulkModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
            </div>
        </AppLayout>
    );
}

function ImportBulkModal({ onClose, onDone }) {
    const [tipo, setTipo] = useState("individuali");
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null); // {processed, total, status}

    const upload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        setProgress({ processed: 0, total: 0, status: "queued" });
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await apiPrivate.post(`/admin/documents/import-excel/${tipo}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const jobId = res.data.job_id;
            // Poll job status ogni 2s finché non completed/failed
            const pollInterval = setInterval(async () => {
                try {
                    const jr = await apiPrivate.get(`/admin/documents/import-jobs/${jobId}`);
                    const j = jr.data;
                    setProgress({ processed: j.progress?.processed || 0, total: j.progress?.total || 0, status: j.status });
                    if (j.status === "completed") {
                        clearInterval(pollInterval);
                        setUploading(false);
                        setResult({
                            created: j.result?.created || 0,
                            skipped: j.result?.skipped || 0,
                            errors: j.errors || [],
                        });
                        toast.success(`Import completato: ${j.result?.created || 0} documenti caricati`);
                    } else if (j.status === "failed") {
                        clearInterval(pollInterval);
                        setUploading(false);
                        setResult({ error: j.result?.error || "Import fallito" });
                    }
                } catch (e) {
                    // ignora errori transient di polling
                }
            }, 2000);
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || "Errore" });
            setUploading(false);
        }
    };

    const tipoConfig = tipo === "individuali"
        ? { title: "Documenti individuali", headers: "email, title, file (URL)", desc: "Ogni riga = documento inviato a un singolo utente (identificato per email)." }
        : { title: "Documenti di gruppo", headers: "Gruppo (es. \"soci A\"), title, file (URL)", desc: "Ogni riga = documento visibile a tutti i soci con una determinata quota (A/B/C/D)." };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="import-docs-modal">
            <div className="bg-[#0a0a0a] border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <span className="label-tag">— Import bulk</span>
                            <h2 className="font-display text-3xl mt-2 tracking-tighter">Importa documenti da Excel.</h2>
                            <p className="text-xs text-acid mt-2 font-grotesk">Nessuna email o notifica verrà inviata ai soci durante l&apos;import.</p>
                        </div>
                        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
                    </div>

                    {/* Tab selector */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                            onClick={() => { setTipo("individuali"); setFile(null); setResult(null); }}
                            className={`p-3 rounded-xl border-2 text-left transition ${tipo === "individuali" ? "border-magenta bg-magenta/10" : "border-zinc-800 hover:border-zinc-700"}`}
                            data-testid="tab-docs-individuali"
                        >
                            <div className="font-display text-lg">Individuali</div>
                            <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">1 riga = 1 documento per 1 utente</div>
                        </button>
                        <button
                            onClick={() => { setTipo("gruppo"); setFile(null); setResult(null); }}
                            className={`p-3 rounded-xl border-2 text-left transition ${tipo === "gruppo" ? "border-magenta bg-magenta/10" : "border-zinc-800 hover:border-zinc-700"}`}
                            data-testid="tab-docs-gruppo"
                        >
                            <div className="font-display text-lg">Di gruppo</div>
                            <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">1 riga = documento visibile a tutti i soci di una categoria</div>
                        </button>
                    </div>

                    <p className="text-zinc-400 mt-4 text-xs font-grotesk">Colonne previste ({tipoConfig.title}): <code className="text-acid">{tipoConfig.headers}</code></p>
                    <p className="text-zinc-500 mt-2 text-xs font-grotesk">{tipoConfig.desc}</p>

                    <label htmlFor="import-docs-file" className="block mt-6 border-2 border-dashed border-zinc-800 p-10 text-center cursor-pointer hover:border-acid transition" data-testid="dropzone-docs">
                        <Upload className="h-8 w-8 mx-auto text-zinc-500" />
                        <div className="mt-3 font-grotesk text-sm">{file ? file.name : `Seleziona il file .xlsx (${tipoConfig.title})`}</div>
                    </label>
                    <input id="import-docs-file" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-docs-input" />

                    {/* Progress bar durante l'import in background */}
                    {progress && uploading && (
                        <div className="mt-4 border border-magenta/40 bg-magenta/5 p-4 font-grotesk text-sm" data-testid="import-docs-progress">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-magenta uppercase tracking-widest text-xs">
                                    {progress.status === "queued" ? "In coda…" : progress.status === "running" ? "Elaborazione in corso" : progress.status}
                                </span>
                                <span className="text-zinc-400 text-xs">
                                    {progress.processed}/{progress.total || "?"} righe
                                </span>
                            </div>
                            <div className="w-full bg-zinc-900 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-magenta h-full transition-all duration-500"
                                    style={{ width: progress.total > 0 ? `${Math.min(100, (progress.processed / progress.total) * 100)}%` : "5%" }}
                                />
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-2">Puoi chiudere questa finestra: l&apos;import continuerà in background.</p>
                        </div>
                    )}

                    {result && (
                        <div className="mt-4 border border-acid/40 bg-acid/5 p-4 font-grotesk text-sm" data-testid="import-docs-result">
                            {result.error ? <span className="text-magenta">{result.error}</span> : (
                                <>
                                    ✓ <strong>{result.created}</strong> documenti caricati · <strong>{result.skipped}</strong> saltati (già presenti)
                                    {result.errors?.length > 0 && (
                                        <div className="mt-2 text-magenta">
                                            <strong>{result.errors.length}</strong> errori:
                                            <ul className="mt-1 text-xs max-h-32 overflow-y-auto">
                                                {result.errors.slice(0, 10).map((e) => <li key={`err-${e.row}`}>Riga {e.row}: {e.error}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 mt-6">
                        <button onClick={upload} disabled={!file || uploading} className="btn-rebel flex-1 justify-center" data-testid="import-docs-submit">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Carica ${tipoConfig.title}`}
                        </button>
                        <button onClick={onDone} className="btn-ghost" data-testid="import-docs-close">Chiudi</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function UploadModal({ onClose, onDone }) {
    const [soci, setSoci] = useState([]);
    const [form, setForm] = useState({
        title: "", category: "comunicazioni", visibility_type: "group", quota_groups: [], owner_id: "", description: "",
    });
    const [file, setFile] = useState(null);
    const [err, setErr] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        apiPrivate.get("/admin/soci?limit=500").then(r => setSoci(r.data));
    }, []);

    const submit = async () => {
        setErr(null); setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", form.title);
            fd.append("category", form.category);
            fd.append("visibility_type", form.visibility_type);
            fd.append("quota_groups", form.quota_groups.join(","));
            if (form.owner_id) fd.append("owner_id", form.owner_id);
            if (form.description) fd.append("description", form.description);
            await apiPrivate.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onDone();
        } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setUploading(false); }
    };

    const recipientsPreview = form.visibility_type === "group"
        ? soci.filter(s => s.quota_categories?.some(q => form.quota_groups.includes(q))).length
        : (form.owner_id ? 1 : 0);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="upload-doc-modal">
            <div className="bg-[#0a0a0a] border border-zinc-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex items-start justify-between">
                        <div>
                            <span className="label-tag">— Nuovo documento</span>
                            <h2 className="font-display text-3xl tracking-tighter mt-2">Pubblica e notifica.</h2>
                        </div>
                        <button onClick={onClose} data-testid="upload-close"><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>

                    <div className="mt-6 space-y-4">
                        <div>
                            <div className="label-tag mb-2">Titolo *</div>
                            <input value={form.title} onChange={(e) => setForm(s => ({ ...s, title: e.target.value }))} data-testid="doc-title" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-acid" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="label-tag mb-2">Categoria</div>
                                <select value={form.category} onChange={(e) => setForm(s => ({ ...s, category: e.target.value }))} data-testid="doc-category" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none">
                                    {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            </div>
                            <div>
                                <div className="label-tag mb-2">Tipo visibilità</div>
                                <select value={form.visibility_type} onChange={(e) => setForm(s => ({ ...s, visibility_type: e.target.value }))} data-testid="doc-visibility" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none">
                                    <option value="group">Gruppo quota</option>
                                    <option value="individual">Individuale (singolo socio)</option>
                                </select>
                            </div>
                        </div>
                        {form.visibility_type === "group" ? (
                            <div>
                                <div className="label-tag mb-2">Gruppi destinatari</div>
                                <div className="flex gap-2">
                                    {["A", "B", "C", "D"].map(q => (
                                        <button key={q} onClick={() => setForm(s => ({ ...s, quota_groups: s.quota_groups.includes(q) ? s.quota_groups.filter(x => x !== q) : [...s.quota_groups, q] }))} data-testid={`doc-group-${q}`} className={`px-4 py-2 text-xs uppercase tracking-widest font-grotesk border ${form.quota_groups.includes(q) ? "border-acid bg-acid text-black" : "border-zinc-800 text-zinc-500"}`}>Quota {q}</button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="label-tag mb-2">Socio destinatario</div>
                                <select value={form.owner_id} onChange={(e) => setForm(s => ({ ...s, owner_id: e.target.value }))} data-testid="doc-owner" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none">
                                    <option value="">Seleziona socio…</option>
                                    {soci.map(s => <option key={s.id} value={s.id}>{(s.ragione_sociale || `${s.nome || ""} ${s.cognome || ""}`).trim()} — {s.email}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <div className="label-tag mb-2">Descrizione (opzionale)</div>
                            <textarea rows={3} value={form.description} onChange={(e) => setForm(s => ({ ...s, description: e.target.value }))} data-testid="doc-description" className="w-full bg-[#0F0F0F] border border-zinc-800 text-white p-3 font-grotesk focus:outline-none focus:border-acid resize-none" />
                        </div>
                        <label htmlFor="doc-file" className="block border-2 border-dashed border-zinc-800 p-8 text-center cursor-pointer hover:border-acid transition" data-testid="doc-dropzone">
                            <Upload className="h-6 w-6 mx-auto text-zinc-500" />
                            <div className="mt-3 font-grotesk text-sm">{file ? file.name : "Trascina o clicca: PDF / Office / immagini (max 50MB)"}</div>
                        </label>
                        <input id="doc-file" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" />

                        <div className="bento p-4 text-sm font-grotesk" data-testid="recipients-preview">
                            <span className="label-tag">Anteprima destinatari</span>
                            <div className="mt-2 text-acid font-bold text-2xl">{recipientsPreview} {recipientsPreview === 1 ? "socio" : "soci"}</div>
                        </div>

                        {err && <div className="border border-magenta/40 bg-magenta/10 text-magenta px-3 py-2 text-sm" data-testid="upload-err">{err}</div>}

                        <button onClick={submit} disabled={!file || !form.title || uploading} className="btn-rebel w-full justify-center" data-testid="upload-submit">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pubblica documento"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
